import {
  collectionMetafields,
  productPackMetafields,
  productRefillMetafields,
  productVariantMetafields,
} from '~/lib/shopify/constants/metafields'
import { getStorePublicCredentials } from '~/lib/shopify/credentials/public'

import {
  GID,
  UnlikelyProduct,
  sfProduct,
  sfProductsByIds,
} from '@unlikelystudio/commerce-connector'

import { getLang } from '~/utils/locales'
import { objectValues } from '~/utils/object-values'

interface GetProductFromShopifyParams {
  locale: string
  id: GID
  shouldFetchCollections?: boolean
  shouldFetchImages?: boolean
  shouldFetchPackMetafields?: boolean
  shouldFetchRefill?: boolean
}

/**
 * It takes a product ID and a locale, and returns the product data for that product ID and locale
 * @param {GetProductFromShopifyParams}  - `locale`: The locale of the store.
 * @returns A product object.
 */
export async function getProductFromShopify({
  locale,
  id,
  shouldFetchCollections,
  shouldFetchImages,
  shouldFetchPackMetafields = false,
  shouldFetchRefill = false,
}: GetProductFromShopifyParams) {
  try {
    const shop = getStorePublicCredentials()
    const languageCode = getLang(locale)?.toUpperCase()
    if (!id) return null
    const productMetafields = [
      ...(shouldFetchPackMetafields ? objectValues(productPackMetafields) : []),
      ...(shouldFetchRefill ? objectValues(productRefillMetafields) : []),
    ]

    const { product } = await sfProduct(
      shop,
      {
        id,
        metafieldKeys: productMetafields,
        includeTags: true,
        metafieldVariantsKeys: objectValues(productVariantMetafields),
        imagesFirst: shouldFetchImages ? 100 : 0,
        collectionsFirst: shouldFetchCollections ? 25 : 0,
        includeFeaturedImage: true,
        variantsFirst: 100,
        includePrices: true,
        includeSelectedOptions: true,
        metafieldCollectionsKeys: objectValues(collectionMetafields),
      },
      languageCode,
    )

    return product
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getProductsFromShopify({
  locale,
  ids,
  includeFeedProducts = false,
}): Promise<UnlikelyProduct[]> {
  try {
    const languageCode = getLang(locale)?.toUpperCase()
    const shop = getStorePublicCredentials()

    const productMetafields = [
      ...objectValues(productPackMetafields),
      ...objectValues(productRefillMetafields),
    ]
    if (!ids || ids.length === 0) return null

    const { products } = await sfProductsByIds(
      shop,
      {
        ids,
        includeTags: true,
        includeWeight: includeFeedProducts,
        metafieldVariantsKeys: objectValues(productVariantMetafields),
        includeDescriptionHtml: includeFeedProducts,
        metafieldKeys: productMetafields,
        includePrices: true,
        variantsFirst: 100,
        imagesFirst: 100,
        collectionsFirst: 25,
        includeSelectedOptions: true,
        metafieldCollectionsKeys: objectValues(collectionMetafields),
      },
      languageCode,
    )

    return products
  } catch (error) {
    console.error(error)
    return null
  }
}

export async function getRefillFromShopify({ locale, id }) {
  try {
    const shop = getStorePublicCredentials()
    const languageCode = getLang(locale)?.toUpperCase()
    if (!id) return null

    const { product } = await sfProduct(
      shop,
      { id, includeFeaturedImage: true },
      languageCode,
    )

    return product
  } catch (error) {
    console.error(error)
    return null
  }
}
